import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {Field, change, untouch} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {
    NumberInput,
    regex,
    minValue
} from 'admin-on-rest';
import {RESOURCES as locationResources} from '../fuseLocationsResourceMap';
import {checkTokenStatus} from '../shared/tokenFunctions';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import FlatButton from 'material-ui/FlatButton';
import ContentAdd from 'material-ui/svg-icons/content/add';
import {Card, CardHeader, CardText} from 'material-ui/Card';
import {
    Table,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
    TableBody
} from "material-ui";
import PriceLevelPriceTableRow from '../components/PriceLevelPriceTableRow';
import FuseTheme from "../global/theme";

/**
 * A component for generating rows of PriceLevelPriceTableRows
 *
 */
class PriceLevelPriceTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            price: {},
            priceLevelPriceList: {},
            priceLevelChoices: [],
            showCreateForm: false,
            priceLevelNameValue: null,
            priceLevelPriceValue: null,
        };
        this.handleCreateNewPriceItem = this.handleCreateNewPriceItem.bind(this);
        this.handleDeleteItem = this.handleDeleteItem.bind(this);
    }

    componentWillMount() {
        this.setState({
            priceLevelPriceList: this.props.priceLevelPriceList,
            price: this.props.price
        });
        this.fetchPriceLevelData();
    }

    componentWillReceiveProps(nextProps) {
        if (
            nextProps.priceLevelPriceList !== this.props.priceLevelPriceList ||
            nextProps.price !== this.props.price
        ) {
            this.setState({
                priceLevelPriceList: nextProps.priceLevelPriceList,
                price: this.props.price
            });
        }
    }

    // Get Price Levels for adding a Price Level
    fetchPriceLevelData() {
        const params = {
            filter: {},
            pagination: {page: 1, perPage: 200},
            sort: {field: "name", order: "ASC"}
        };
        const resource = locationResources.PriceLevels.GET_LIST(params);

        // Retrieve the data and assign results to the Select choices
        import('../shared/functions')
            .then(functions => {
                const tokenStatus = checkTokenStatus(null);
                if (tokenStatus.expired) {
                    return;
                }
                axios({
                    method: 'get',
                    url: resource.uri,
                    headers: functions.getGetHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        let choices = this.setChoiceKeys(result.data.payload);
                        this.setState({
                            priceLevelChoices: choices
                        });
                    }
                }).catch((error) => {
                    let message = '';
                    if (error.response) {
                        // Server response outside 2xx
                        console.log(error.response.data);
                        if (error.response.data.error_message) {
                            message = error.response.data.error_message;
                            console.log('Error: ' + message);
                        }
                    } else if (error.request) {
                        // No response
                        console.log(error.request);
                    } else {
                        console.log('Error', error.message);
                    }
                });
            });
    }

    // Add unique keys for Price Level choices
    setChoiceKeys = (choices) => {
        for (let choice of choices) {
            choice.id = choice.name;
        }
        return choices;
    };

    // Handle event to display/hide Add Price fields
    handleToggleClick = (e) => {
        this.setState({
            showCreateForm: true,
        });
    };

    // Create a new Price item based on field values
    handleCreateNewPriceItem = (e) => {

        const priceLevelName = this.props.priceLevelNameValue;
        const priceLevelPrice = this.props.priceLevelPriceValue;
        if (!priceLevelName || !priceLevelPrice) {
            return;
        }

        // Set the component list and the record
        let priceLevelPriceList = this.props.priceLevelPriceList;
        priceLevelPriceList.set(priceLevelName, priceLevelPrice.toString());

        let priceList = JSON.parse(JSON.stringify(this.props.price));
        priceList[priceLevelName] = priceLevelPrice.toString();

        this.setState({
            price: priceList,
            priceLevelPriceList: priceLevelPriceList,
            priceLevelNameValue: null,
            priceLevelPriceValue: null,
            showCreateForm: false,
        }, () => {
            this.props.dispatch(change('record-form', 'price.' + priceLevelName, priceLevelPrice.toString()));
            this.props.onAddUpdate({[priceLevelName]: priceLevelPrice.toString()});

            // Reset the fields
            this.props.dispatch(change('record-form', 'priceLevelSelectField', ''));
            this.props.dispatch(untouch('record-form', 'priceLevelSelectField'));
            this.props.dispatch(change('record-form', 'priceLevelPriceField', ''));
            this.props.dispatch(untouch('record-form', 'priceLevelPriceField'));
        });
    };

    // Handle event from item Delete icon
    handleDeleteItem = (key) => {
        // Set the component list and the record
        let priceLevelPriceList = this.props.priceLevelPriceList;
        let priceList = JSON.parse(JSON.stringify(this.props.price));

        // Remove the key if it exists
        priceLevelPriceList.delete(key);
        priceList[key] = undefined;

        this.setState({
            price: priceList,
            priceLevelPriceList: priceLevelPriceList,
            priceLevelNameValue: null,
            priceLevelPriceValue: null,
            showCreateForm: (priceLevelPriceList.size === 0),
        }, () => {
            this.props.dispatch(change('record-form', 'price.' + key, ''));
            this.props.onDeleteUpdate(key);
        });
    };

    // Create table rows of Price Level Price items
    makePriceTable = (list, showDelete, deleteClickFunc) => {
        if (!list || list.size === 0 || !list.size) {
            return (
                <TableRow>
                    <TableRowColumn>No Price Level Prices</TableRowColumn>
                    <TableRowColumn>&nbsp;</TableRowColumn>
                    <TableRowColumn>&nbsp;</TableRowColumn>
                </TableRow>
            )
        }
        const rows = function* () {
            for (const [key, value] of list) {
                let priceValue = (value) ? value.toString() : '';
                if (priceValue) {
                    yield (
                        <PriceLevelPriceTableRow
                            key={key.toString()}
                            price_level={key.toString()}
                            price={priceValue}
                            showDelete={showDelete}
                            onDeleteClick={deleteClickFunc}/>
                    )
                }
            }
        };
        return rows();
    };

    render() {
        const styles = {
            addBlock: {padding: '8px 0 0 16px'},
            alignBottom: {verticalAlign: 'bottom'},
            alignTop: {verticalAlign: 'top'},
            block: {display: 'block'},
            fieldDescription: {
                display: 'block',
                width: '100%',
                fontSize: '14px',
                color: FuseTheme.palette.lightTextColor,
                margin: '0 0 1em 0',
            },
            fieldSmall: {display: 'inline-block', maxWidth: 80},
            fieldCreate: {display: 'inline-block', maxWidth: 120, margin: '0 1em 0 0', verticalAlign: 'top'},
            icon: {height: '40px', padding: '10px', overflow: 'inherit'},
            rowHeight: {height: '40px'},
            tableCard: {display: 'block', margin: '1em 0 0 0'},
        };
        return (
            <Card style={styles.tableCard}>
                <CardHeader title="Price Level Prices" actAsExpander={true} showExpandableButton={true}/>
                <CardText expandable={true}>
                    {this.props.showCreate &&
                    <span style={styles.fieldDescription}>Add or Remove Price Level Prices. These are Prices that
                    override the Default Price according to a selected Price Level.</span>
                    }
                    <Table selectable={false} multiSelectable={false}>
                        <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                            <TableRow>
                                <TableHeaderColumn>Price Level</TableHeaderColumn>
                                <TableHeaderColumn>Price</TableHeaderColumn>
                                <TableHeaderColumn>Remove</TableHeaderColumn>
                            </TableRow>
                        </TableHeader>
                        <TableBody displayRowCheckbox={false}>
                            {[...this.makePriceTable(this.props.priceLevelPriceList, this.props.showDelete, this.handleDeleteItem)]}
                        </TableBody>
                    </Table>
                    {this.props.showCreate &&
                    <div style={styles.addBlock}>
                        {!this.state.showCreateForm &&
                        <FlatButton
                            primary
                            label="Add a Price Level Price"
                            icon={<ContentAdd/>}
                            onClick={(e) => this.handleToggleClick(e)}
                        />
                        }
                        {this.state.showCreateForm &&
                        <div>
                            <Field name="priceLevelSelectField"
                                   component={VirtualizedSelectInput}
                                   choices={this.state.priceLevelChoices}
                                   label="Price Level"
                                   optionText="name"
                                   defaultValue={this.state.priceLevelNameValue}
                                   style={styles.fieldCreate}
                                   elStyle={styles.fieldCreate}
                                   allowEmpty
                                   options={{
                                       className: "inline",
                                       placeholder: "Price Level",
                                   }}
                            />
                            <Field name="priceLevelPriceField"
                                   label="Price"
                                   component={NumberInput}
                                   step="any"
                                   validate={[regex(/^\d*(\.\d{0,2})?$/, 'Must be a valid price.'), minValue(0, 'Must be a positive amount.')]}
                                   elStyle={styles.fieldCreate}
                                   defaultValue={this.state.priceLevelPriceValue}
                            />
                            <FlatButton
                                primary
                                label="Add"
                                icon={<ContentAdd/>}
                                onClick={(e) => this.handleCreateNewPriceItem(e)}
                                style={styles.alignBottom}
                            />
                        </div>
                        }
                    </div>
                    }
                </CardText>
            </Card>
        )
    }
}

const mapStateToProps = (state, ownProps) => {
    let form = state.form['record-form'];
    if (form && form.values) {
        const selectValue = (form.values['priceLevelSelectField']) ? form.values['priceLevelSelectField'].toString() : null;
        const priceValue = (form.values['priceLevelPriceField']) ? form.values['priceLevelPriceField'].toString() : null;
        return {
            price: ownProps.price,
            priceLevelPriceList: ownProps.priceLevelPriceList,
            priceLevelNameValue: selectValue,
            priceLevelPriceValue: priceValue
        }
    }
    return {
        priceLevelNameValue: null,
        priceLevelPriceValue: null
    }
};

PriceLevelPriceTable.propTypes = {
    addField: PropTypes.bool,
    onAddUpdate: PropTypes.func,
    onDeleteUpdate: PropTypes.func,
    price: PropTypes.object,
    priceLevelPriceList: PropTypes.object.isRequired,
    priceLevelNameValue: PropTypes.string,
    priceLevelPriceValue: PropTypes.string,
    showCreate: PropTypes.bool,
    showDelete: PropTypes.bool,
    showCreateForm: PropTypes.bool,
};

PriceLevelPriceTable.defaultProps = {
    addField: true,
    price: {},
    priceLevelPriceList: new Map(),
    priceLevelNameValue: null,
    priceLevelPriceValue: null,
    showCreate: false,
    showDelete: false,
    showCreateForm: false,
};

const enhance = compose(
    connect(mapStateToProps)
);

export default enhance(PriceLevelPriceTable);



// WEBPACK FOOTER //
// ./src/Fuse/components/PriceLevelPriceTable.js